import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Button
} from '@mui/material';
import { PhotoCamera, Pets } from '@mui/icons-material';
import { PetProfile } from '../types/onboarding';
import { calculateBirthdayFromAge } from '../utils/birthdayCalculation';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { IntakeForm } from './IntakeForm';

interface PetEditDialogProps {
  open: boolean;
  pet: PetProfile | null;
  userId: string;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
  onClose: () => void;
  onSave: (updatedPet: PetProfile) => void;
  embedded?: boolean;
}

export const PetEditDialog: React.FC<PetEditDialogProps> = ({
  open,
  pet,
  userId,
  userLanguage,
  onClose,
  onSave,
  embedded = false
}) => {
  const [profilePhoto, setProfilePhoto] = useState(pet?.profilePhoto || '');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync photo with pet prop when it changes
  useEffect(() => {
    if (pet) {
      setProfilePhoto(pet.profilePhoto || '');
    }
  }, [pet]);

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "Edit Pet Profile",
        save: "Save Changes",
        cancel: "Cancel",
        saving: "Saving...",
        error: "Error saving pet profile",
        profilePhoto: "Profile Photo",
        uploading: "Uploading...",
        selectPhoto: "Select Photo",
      },
      'zh-CN': {
        title: "编辑宠物资料",
        save: "保存更改",
        cancel: "取消",
        saving: "保存中...",
        error: "保存宠物资料时出错",
        profilePhoto: "宠物照片",
        uploading: "上传中...",
        selectPhoto: "选择照片",
      },
      es: {
        title: "Editar Perfil de Mascota",
        save: "Guardar Cambios",
        cancel: "Cancelar",
        saving: "Guardando...",
        error: "Error al guardar el perfil de mascota",
        profilePhoto: "Foto de la Mascota",
        uploading: "Subiendo...",
        selectPhoto: "Seleccionar Foto",
      },
      fr: {
        title: "Modifier le Profil de l'Animal",
        save: "Enregistrer les Modifications",
        cancel: "Annuler",
        saving: "Enregistrement...",
        error: "Erreur lors de l'enregistrement du profil de l'animal",
        profilePhoto: "Photo de l'Animal",
        uploading: "Téléchargement...",
        selectPhoto: "Sélectionner Photo",
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    setError(null);

    try {
      const convertToCompressedDataURL = (file: File, maxWidth: number = 200, quality: number = 0.8): Promise<string> => {
        return new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.onload = () => {
            let { width, height } = img;
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
            ctx?.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      };

      try {
        const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `pet-photos/${userId}/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        setProfilePhoto(downloadURL);
      } catch (storageError) {
        const compressedDataURL = await convertToCompressedDataURL(file, 200, 0.7);
        setProfilePhoto(compressedDataURL);
      }
    } catch (error) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async (intakeData: Partial<PetProfile>) => {
    if (!pet?.id) {
      console.error('❌ No pet ID provided');
      setError('No pet ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!intakeData.name?.trim()) {
        setError('Pet name is required');
        return;
      }

      // Prepare data for Firebase (remove undefined values and handle structures)
      const updateData: any = {
        name: intakeData.name.trim(),
        type: intakeData.type || 'dog',
        breed: intakeData.breed?.trim() || null,
        age: intakeData.age !== undefined && intakeData.age !== null ? intakeData.age : null,
        gender: intakeData.gender || 'unknown',
        emergencyContact: intakeData.emergencyContact?.trim() || null,
        vetContact: intakeData.vetContact?.trim() || null,
        boardingDates: intakeData.boardingDates || null,
        foodAllergies: intakeData.foodAllergies || null,
        freshTreatPermission: intakeData.freshTreatPermission !== undefined ? intakeData.freshTreatPermission : null,
        medicalStatus: intakeData.medicalStatus || null,
        medications: intakeData.medications?.trim() || null,
        pottyHabits: intakeData.pottyHabits?.trim() || null,
        barkingHabits: intakeData.barkingHabits?.trim() || null,
        chewingHabits: intakeData.chewingHabits || null,
        separationAnxiety: intakeData.separationAnxiety?.trim() || null,
        favoriteThings: intakeData.favoriteThings || null,
        favoriteThingsOther: intakeData.favoriteThingsOther?.trim() || null,
        walkingPreferences: intakeData.walkingPreferences?.trim() || null,
        specialNeeds: intakeData.specialNeeds?.trim() || null,
        profilePhoto: profilePhoto || null,
        updatedAt: serverTimestamp()
      };

      // Handle birthday calculation from age
      if (updateData.age !== null && updateData.age !== pet.age) {
        try {
          updateData.birthday = calculateBirthdayFromAge(updateData.age);
        } catch (err) {
          console.warn('Failed to calculate birthday from age:', err);
        }
      }

      // Remove null/undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      console.log('📝 Prepared update data for Firestore:', updateData);

      const petRef = doc(db, 'users', userId, 'petProfiles', pet.id);
      
      const docSnap = await getDoc(petRef);
      if (!docSnap.exists()) {
        throw new Error('Pet profile not found. Please refresh and try again.');
      }

      await updateDoc(petRef, updateData);

      const savedPet: PetProfile = {
        ...pet,
        ...updateData,
        // Set updatedAt locally for immediate UI update
        updatedAt: new Date()
      } as any;

      onSave(savedPet);
      onClose();
    } catch (error: any) {
      console.error('❌ Error saving pet profile:', error);

      let errorMessage = content.error;
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your authentication.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Pet profile not found. Please refresh and try again.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data format. Please check your input.';
      } else if (error.message) {
        errorMessage = `Save failed: ${error.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!pet) return null;

  const formContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 1 }}>
        {/* Profile Photo Upload */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {content.profilePhoto}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
              src={profilePhoto}
            >
              {!profilePhoto && <Pets />}
            </Avatar>
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`dialog-pet-photo-upload-${pet.id}`}
                type="file"
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto}
              />
              <label htmlFor={`dialog-pet-photo-upload-${pet.id}`}>
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <PhotoCamera />}
                  disabled={uploadingPhoto}
                  size="small"
                >
                  {uploadingPhoto ? content.uploading : content.selectPhoto}
                </Button>
              </label>
            </Box>
          </Box>
        </Box>

        {/* Embedded Intake Form */}
        <IntakeForm
          onSubmit={handleSave}
          isSubmitting={loading}
          initialData={pet}
          embedded={true}
          onCancel={onClose}
          submitButtonText={content.save}
        />
      </Box>
    </>
  );

  if (embedded) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)',
          },
        }}>
          {formContent}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {content.title}
      </DialogTitle>
      <DialogContent>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};
